import {
  Box,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  ModalFooter,
  AlertDescription,
  Alert,
} from '@chakra-ui/react';
import { useForm, Controller } from 'react-hook-form';
import { Button } from 'components/elements/Button';
import { Text } from 'components/elements/Text';
import { WarningIcon } from 'components/icons/WarningIcon';
import { FlexRow } from 'components/layout/FlexRow';
import { useToast } from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';

type ChangePasswordModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (clickText: string) => void;
};

type FormValues = {
  currentPassword: string;
  newPassword: string;
  confirmNewPassword: string;
};

const SUBMIT_BUTTON_TEXT = 'Save & continue';

export const ChangePassword = ({ isOpen, onSave, onClose }: ChangePasswordModalProps) => {
  const { handleSubmit, control, getValues, formState } = useForm<FormValues>();
  const { editUserPassword } = useUserAccount();
  const { renderCustomToast: toast } = useToast();

  const handleFormSubmit = async (data: FormValues) => {
    const editUserPasswordObj = {
      current_password: data.currentPassword,
      new_password_1: data.newPassword,
      new_password_2: data.confirmNewPassword,
    };
    try {
      await editUserPassword(editUserPasswordObj);
      toast({
        title: 'Password changed successfully',
        status: 'success',
      });
      onSave?.(SUBMIT_BUTTON_TEXT);
      onClose();
    } catch (_) {
      toast({
        title: 'Error changing password',
        status: 'error',
      });
    }
  };

  const validateConfirmNewPassword = (value: string) => {
    const newPassword = getValues('newPassword');

    return value === newPassword || 'Passwords do not match';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Change Password</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(handleFormSubmit)}>
          <ModalBody>
            <FormControl id="currentPassword">
              <FormLabel sx={{ fontSize: '12px', fontWeight: '600' }}>
                Current Password
              </FormLabel>
              <Controller
                name="currentPassword"
                defaultValue=""
                control={control}
                rules={{ required: true }}
                render={({ field }) => <Input type="password" {...field} />}
              />
            </FormControl>
            <FormControl id="newPassword" mt={4}>
              <FormLabel sx={{ fontSize: '12px', fontWeight: '600' }}>
                New Password
              </FormLabel>
              <Controller
                name="newPassword"
                defaultValue=""
                control={control}
                rules={{ required: true }}
                render={({ field }) => <Input type="password" {...field} />}
              />
            </FormControl>
            <FormControl id="confirmNewPassword" mt={4}>
              <FormLabel sx={{ fontSize: '12px', fontWeight: '600' }}>
                Confirm New Password
              </FormLabel>
              <Controller
                name="confirmNewPassword"
                defaultValue=""
                control={control}
                rules={{
                  required: true,
                  validate: validateConfirmNewPassword,
                }}
                render={({ field }) => <Input type="password" {...field} />}
              />
            </FormControl>
            <Alert
              status="error"
              display={formState?.errors?.confirmNewPassword?.message ? 'flex' : 'none'}
              sx={{
                padding: '4px',
                marginTop: '8px',
                backgroundColor: '#fef4ee',
                borderRadius: '4px',
                width: 'fit-content',
              }}
            >
              <AlertDescription sx={{ color: '#FF6813' }}>
                <FlexRow sx={{ gap: '6px' }}>
                  <WarningIcon />
                  <Text
                    sx={{
                      fontSize: '12px',
                      fontWeight: '600',
                      letterSpacing: '-2%',
                    }}
                  >
                    {formState.errors.confirmNewPassword?.message}
                  </Text>
                </FlexRow>
              </AlertDescription>
            </Alert>
          </ModalBody>
          <ModalFooter>
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <Button
                color="secondary"
                onClick={() => onClose()}
                sx={{ mr: '8px', p: '16px', height: 'fit-content' }}
              >
                Cancel
              </Button>
              <Button
                color="primary"
                type="submit"
                sx={{ px: '32px', py: '16px', height: 'fit-content' }}
              >
                {SUBMIT_BUTTON_TEXT}
              </Button>
            </Box>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
};
